import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import ReactToPrint from 'react-to-print';
import { getFixedNumber } from 'utils/general';

import { useGetSettlementDetailsPrint } from '@/modules/Settlements/apiHooks';
import Header from '@/modules/Settlements/components/Details/shared/Header/ActionsButtons/PrintSettlementAction/Header';
import {
  getSerializedPrintedData,
  settlementsPrintColumns,
} from '@/modules/Settlements/components/Details/shared/Header/ActionsButtons/PrintSettlementAction/logic';
import { ISettlements } from '@/modules/Settlements/types';
import { Button, Table, Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import PrintSettlementIcon from '~/images/shared/Printer.svg';
// TODO: refactor this file & logic
const PrintSettlementAction = () => {
  const qrcodeRef = React.useRef(null);
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const { openModal } = useModal();
  const { query } = useRouter();
  const { id } = query;
  const [store] = useStore((store) => store);
  const selectedAsset = store?.asset;
  const { data, isLoading } = useGetSettlementDetailsPrint({
    entityId: store?.isDemoFarm ? undefined : selectedAsset?.id,
    id: id?.toString(),
    type: selectedAsset?.settlementLevel,
  });
  const serializedPrintedData =
    selectedAsset?.settlementLevel === 2
      ? (data?.days as ISettlements.IWorkerSettlementDaySchema[]) ?? []
      : getSerializedPrintedData({
          days: (data?.days as ISettlements.ISettlementDetailsDay[]) || [],
          t,
        });

  return (
    <div
      onClick={
        store?.isDemoFarm
          ? () =>
              openModal(
                <DemoCTA redirectRouteBeforeExit={routes.settlements} />
              )
          : undefined
      }
    >
      <ReactToPrint
        removeAfterPrint
        fonts={[{ family: 'Cairo', source: '' }]}
        content={() => qrcodeRef.current}
        pageStyle='@media print {
          @page {
            size: A4 landscape;
            margin:0mm;
            margin-bottom:1cm;
          }
        
       }'
        trigger={() => (
          <Button
            className='flex items-center justify-center gap-8'
            loading={selectedAsset?.id !== -1 && isLoading}
          >
            <>
              <BaseIcon size='sm'>
                <PrintSettlementIcon />
              </BaseIcon>
              {t('print')}
            </>
          </Button>
        )}
      />
      {/* printed settlements */}
      <div className='hidden'>
        {data && (
          <div
            ref={qrcodeRef}
            className='block p-16  print:break-inside-avoid-page '
            id='settlementsPrintedPage'
          >
            <Table<
              | ISettlements.IWorkerSettlementDaySchema
              | ISettlements.ISettlementDetailsDayInstance
            >
              renderComponentBeforHead={
                <Header
                  issuanceDate={data?.issuanceDate}
                  supplierName={
                    selectedAsset?.settlementLevel === 2
                      ? (data as ISettlements.IGetWorkerSettlementDetails)
                          ?.crewName
                      : (data as ISettlements.IGetSettlementDetails)
                          ?.supplierName
                  }
                  startDate={data?.startDate}
                  endDate={data?.endDate}
                  workerName={
                    selectedAsset?.settlementLevel === 2
                      ? (data as ISettlements.IGetWorkerSettlementDetails)
                          ?.workerName
                      : undefined
                  }
                />
              }
              rowCellClassNameFu={(row) =>
                'lastInstanceInDate' in row && row.lastInstanceInDate
                  ? 'border-b-xs border-background-200'
                  : ''
              }
              headContainerClassName={
                language === 'ar' ? 'text-right' : 'text-left'
              }
              headCellClassName='!py-1 !px-2 bg-background-neutral-100 rtl:first:rounded-tr-lg rtl:last:rounded-tl-lg ltr:first:rounded-tl-lg ltr:last:rounded-tr-lg'
              tableWrapperClassName='whitespace-normal mt-16 '
              selection={false}
              columns={settlementsPrintColumns(
                t,
                selectedAsset?.currency || '',
                selectedAsset?.settlementLevel
              )}
              // filter data in case wasred time and total hours 0
              data={serializedPrintedData}
              pagination={false}
              footerContent={
                <div className='fixed bottom-0 px-1'>{t('accounts')} :</div>
              }
              footerClassName='h-24'
            />
            <div className='flex w-full items-center justify-between gap-12 rounded-b-lg bg-background-neutral-100 py-1 px-2'>
              <Typography variant='caption'>
                {t('totalDailyDuesDetails')}
              </Typography>
              <Typography variant='subtitle2' weight='bold'>
                {getFixedNumber(data.totalAmount) || '-'}
              </Typography>
            </div>
            {/* ------------- */}
            <div className=' my-14 flex items-center justify-between rounded-lg bg-primary-default px-16 py-9'>
              <Typography variant='body1' className='text-white'>
                {t('net-settlement')}
              </Typography>
              <Typography variant='body1' weight='bold' className='text-white'>
                {getFixedNumber(
                  selectedAsset?.settlementLevel === 2
                    ? data.totalAmount
                    : (data as ISettlements.IGetSettlementDetails)
                        .totalDaysAmount
                ) || '-'}{' '}
                {selectedAsset?.currency}
              </Typography>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default PrintSettlementAction;
